import React, { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useDemoStatus } from '@/hooks/useAuth';
import { OPEN_ONBOARDING_EVENT, Pages } from '@/components/modals/OnboardingDialog';

interface DemoSignupBannerProps {
  variant: 'app' | 'dashboard';
  className?: string;
}

const demoStatuses = new Set(['DEMO']);

export const DemoSignupBanner: React.FC<DemoSignupBannerProps> = ({ variant, className }) => {
  // Don't show banner if app is embedded in an iframe
  const isEmbedded = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.self !== window.top;
  }, []);

  const { t } = useTranslation('plans');
  const status = useDemoStatus();
  const isDemoUser = status ? demoStatuses.has(status.toUpperCase()) : false;

  const handleSignup = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (typeof window === 'undefined') {
        return;
      }
      const detail = { page: Pages.SignUp };
      const signupEvent = new CustomEvent(OPEN_ONBOARDING_EVENT, { detail });
      window.dispatchEvent(signupEvent);
    },
    []
  );

  if (!isDemoUser || isEmbedded) {
    return null;
  }

  const leadingCopy = variant === 'app' ? t('demo.banner') : null;
  const supportingCopy = t('demo.saveData');

  return (
    <div
      className={cn(
        'w-full border-b border-amber-200 bg-amber-100/90 px-4 py-2 text-sm text-amber-900',
        'flex flex-col items-start gap-1 md:flex-row md:items-center md:justify-between',
        className
      )}
      role="status"
      aria-live="polite"
    >
      {leadingCopy ? (
        <span className="font-medium">{leadingCopy}</span>
      ) : null}
      <span className="text-sm text-amber-900">
        {supportingCopy}{' '}
        <button
          data-event-click="conversion.demo.banner.click"
          type="button"
          onClick={handleSignup}
          className="font-semibold text-[#1381cd] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-amber-100 hover:text-[#0f6ab5]"
          aria-label={supportingCopy}
        >
          {t('demo.signUp')}
        </button>
      </span>
    </div>
  );
};
